/**
 * 內容層：桌面三欄（清單／對話串＋指令列／右側抽屜），手機一欄一屏。
 *
 * 三欄的尺寸、元件與 `data-testid` 全部沿用改寫前的 App.tsx：
 * 左 300px 清單、中 flex 對話串＋常駐指令列、右 300px 抽屜（`drawerOpen` 控制存在與否）。
 * 這一層只重排「放在哪裡」，不改任何一個子元件的 props 或行為。
 *
 * 手機（<768px）一欄一屏（清單→對話串），房間清單收成一顆「房間」按鈕打開的抽屜，
 * **預設收起、選完自動收**；底部導航由 AppShell 的 BottomNav 提供（不再自繪 tab 列）。
 *
 * 房間只有一份清單（`blocks/RoomSidebar` → console-ui `RoomPanel`）：桌面是右側常駐側欄，
 * 手機是上面那個抽屜，`#/rooms` 用它當左欄並把選中那間房的對話放右邊（#12）。
 */
import { useMemo, useState } from 'react';
import {
  AccountPool,
  SessionBar,
  type SessionBarProps,
  CommandBar,
  ConversationList,
  NoticeStack,
  OpeningPanel,
  ThreadView,
  accountLabel,
  shortAccountId,
  type RoomInfo,
} from '@chat-rea/console-ui';
import type { ConsoleActions } from '../hooks/useConsoleActions';
import type { Section } from './ConsoleShell';
import { routeToUrl } from './routes';
import { LeftRail } from './blocks/LeftRail';
import { RoomSidebar } from './blocks/RoomSidebar';

export interface InboxLayoutProps {
  c: ConsoleActions;
  narrow: boolean;
  section: Section;
  /**
   * 是不是管理員（role 含 admin）。
   *
   * 抽屜的「健康」分頁已退場（連線／站方狀態只在系統區一處），所以本檔不再用它：
   * 送訊息不是管理操作，任何已開身分都應該能送；真正的門在 Worker（`/api/*` 要 session）。
   * 留著 prop 只為不一次動 `App.tsx` 的呼叫端。
   */
  isAdmin?: boolean;
  /** 目前登入者與「重新登入」；內容區最上方那一列（舊的 API Token 列已移除）。 */
  session?: SessionBarProps;
}

export function InboxLayout({ c, narrow, section, session }: InboxLayoutProps) {
  /**
   * 手機的房間抽屜：預設收起（#07 手機兩層的另一半），選完房間自動收。
   * 桌面不靠這個 state（桌面用 `c.drawerOpen` 的常駐側欄）。
   */
  const [roomsDrawerOpen, setRoomsDrawerOpen] = useState(false);

  /**
   * 把「已開身分」接上帳號池的顯示名稱。
   *
   * session 只知道 `account`（歸因帳號）／`nickname`（登入名），顯示名稱在帳號池。
   * 這裡用 `service + username` 對起來，產生：
   *   1. `conversations`：每一列多 `username`／`displayName`（給帳號池列與清單用）
   *   2. `senderNames`：聊天室 sender 對照表（raw `from` → 顯示標籤）
   * 對不起來的（別人的站方暱稱）不改寫、原樣顯示。
   */
  const { conversations, listRows, senderNames, identities } = useMemo(() => {
    const poolIndex = new Map<string, string | null>(
      c.poolAccounts.map((item) => [`${item.service}:${item.username}`, item.displayName ?? null]),
    );
    const names: Record<string, string> = {};
    const rows = c.conversations.map((item) => {
      const username = item.account ?? item.nickname;
      const displayName = poolIndex.get(`${item.service}:${username}`) ?? null;
      // 有名字用名字；沒有就用縮寫（聊天室不掛「未命名」，帳號池才掛）。
      const chatLabel = accountLabel(item.service, username, displayName, { withFallback: false });
      names[item.nickname] = chatLabel;
      names[username] = chatLabel;
      return { ...item, username, displayName, displayLabel: chatLabel };
    });
    // 左側清單只顯示暱稱欄位；蓋成顯示標籤（id／排序都不變，onSelect 用的是 id）。
    const listRows = rows.map((item) => ({ ...item, nickname: item.displayLabel }));
    const identities = rows.map((item) => ({
      id: item.id,
      label: item.displayLabel,
      keys: [item.nickname, item.username],
    }));
    return { conversations: rows, listRows, senderNames: names, identities };
  }, [c.conversations, c.poolAccounts]);

  /** 換一顆走網址，否則舊網址會把選取拉回去。同一顆再點只打開手機那一層。 */
  const openSession = (id: string) => {
    if (id === c.activeId) {
      c.openConversation(id);
      return;
    }
    window.location.hash = routeToUrl({
      section: 'workspace',
      systemTab: 'overview',
      sessionId: id,
      platform: null,
    });
  };

  /**
   * 點一間房＝把主區切到那間房的對話（只有這一份房間清單，#12）。
   *
   * 挑對話的順序：同房已開的那一條（正在看的優先）→ 同房第一條 → 都沒有就把目前身分帶進房。
   * 手機選完就收抽屜，桌面側欄常駐不動。
   */
  const selectRoom = (room: RoomInfo) => {
    setRoomsDrawerOpen(false);
    const inRoom = conversations.filter((item) => item.room === room.id || item.room === room.name);
    const pick = inRoom.find((item) => item.id === c.activeId) ?? inRoom[0];
    if (pick) {
      openSession(pick.id);
      return;
    }
    c.joinRoom(room);
  };

  /** 房間清單：桌面側欄與手機抽屜共用同一個元件、同一份資料。 */
  const roomSidebar = <RoomSidebar c={c} onSelect={selectRoom} selectedRoom={c.active?.room ?? null} />;

  /**
   * 帳號頁「已開的身分」（session）。`AccountPool` 只管顯示與兩段式確認，
   * 三個「全部離開」狀態（`onLeaveAll`／`leaveAllProgress`／`leaveAllResult`）1:1 從
   * `useConsoleActions` 傳進來——**不給就不會出現那顆按鈕**，所以這裡一定要接。
   */
  const sessionsPanel = (
    <AccountPool
      accounts={conversations}
      loading={c.inboxLoading}
      error={c.inboxError}
      errorKind={c.inboxErrorKind}
      onRetry={() => void c.refresh()}
      selectedIds={c.sessionPicked}
      activeAccountId={c.activeId}
      busy={c.busy}
      onSelect={openSession}
      onToggle={c.toggleSessionPick}
      onJoinAll={() => {
        void c.joinSessions(c.targetRoom).catch(() => undefined);
      }}
      targetRoom={c.targetRoom}
      onLeaveAll={() => {
        // 失敗已逐顆寫進結算句與上方橫幅；這裡吞掉 rejection，不留 unhandled。
        void c.leaveAll().catch(() => undefined);
      }}
      leaveAllProgress={c.leaveAllProgress}
      leaveAllResult={c.leaveAllResult}
      onClose={(id) => {
        const target = c.conversations.find((item) => item.id === id);
        if (target) c.deleteAccount(target);
      }}
      onRename={(account, displayName) => c.renameAccount(account, displayName)}
    />
  );

  const commandBar = (
    <CommandBar
      nickname={c.active ? (senderNames[c.active.nickname] ?? c.active.nickname) : ''}
      service={c.active?.service}
      identities={identities}
      busy={c.busy}
      disabled={!c.active}
      placeholder={
        c.active
          ? // 短版：390px 的發送框只有 38px 高，長句會折三行被裁掉（舊字串在手機上露出半行）
            '打字回覆…（Enter 送出，/ 找片語）'
          : '先選一個對話，或按「＋ 建帳號」'
      }
      onSay={c.say}
      onPm={c.sendPm}
      onSwitch={openSession}
      onUseAccount={(username) => c.usePoolAccount(username)}
      poolAccounts={c.poolAccounts.map((account) => account.username)}
    />
  );

  /**
   * 只把「這一間房」的事件交給對話串（問題單 #11）。
   *
   * 後端 `GET /events` 現在預設回目前房間（`room` 參數見 session-do），但 WS 即時事件
   * 沒有房間守門：換房那一瞬間，舊房還在路上的話會被 append 進同一條串。這一層是最後一道。
   * `event.room` 缺值＝後端狀態紀錄（「session 已休眠」這類），照舊顯示，不要藏起來。
   */
  const threadEvents = useMemo(() => {
    const room = c.active?.room ?? null;
    if (!room) return c.activeEvents;
    return c.activeEvents.filter((event) => !event.room || event.room === room);
  }, [c.activeEvents, c.active?.room]);

  const thread = (onBack?: () => void) => (
    <ThreadView
      events={threadEvents}
      title={c.active?.room ?? undefined}
      subtitle={
        c.active
          ? `${senderNames[c.active.nickname] ?? c.active.nickname} · ${c.active.service}`
          : null
      }
      senderNames={senderNames}
      loading={c.eventsLoading}
      // 對話串也有三態（#02）：事件載入失敗說「這段讀不到」＋重試，不是「還沒有訊息」。
      error={c.eventsError}
      errorKind={c.eventsErrorKind}
      onReload={() => {
        if (c.activeId) void c.refreshEvents(c.activeId);
      }}
      keywords={c.highlightKeywords}
      onBack={onBack}
      onRetry={c.retryOutgoing}
      onVerify={c.verifyDelivery}
    />
  );

  const list = (
    <ConversationList
      conversations={listRows}
      activeId={c.activeId}
      onSelect={openSession}
      keywords={c.highlightKeywords}
      // 三態一律由 useInbox 傳下來：斷線／401 時左欄說「連不上」並給重試，
      // 不能落回「還沒有對話；先建帳號並進房」（#02）。
      loading={c.inboxLoading}
      loaded={c.inboxLoaded}
      error={c.inboxError}
      errorKind={c.inboxErrorKind}
      onRetry={() => void c.refresh()}
    />
  );

  /**
   * 手機的房間抽屜（#07）：預設收起，選完房間由 `selectRoom` 收起來。
   * 桌面不用這個（桌面是常駐側欄），所以只在 `narrow` 時掛。
   */
  const roomsDrawer = (
    <div className="fixed inset-0 z-40 flex" data-testid="room-drawer">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="關閉房間清單"
        onClick={() => setRoomsDrawerOpen(false)}
        data-testid="room-drawer-backdrop"
      />
      <aside
        className="relative flex h-full w-[86%] max-w-[340px] flex-col gap-2 border-r border-border bg-background p-2 shadow-xl"
        aria-label="房間"
      >
        {/* 標題由面板自己畫（不要出現兩個「房間」）；這裡只留關閉鍵。 */}
        <div className="flex flex-none justify-end">
          <button
            type="button"
            className="cui-icon-btn"
            onClick={() => setRoomsDrawerOpen(false)}
            aria-label="關閉"
            data-testid="room-drawer-close"
          >
            ×
          </button>
        </div>
        {roomSidebar}
      </aside>
    </div>
  );

  /** 手機頂部那一顆「房間 n」：抽屜的開關（收起時房間清單只剩這一顆）。 */
  const roomsToggle = (
    <div className="flex flex-none items-center gap-2">
      <button
        type="button"
        className="cui-btn cui-btn--sm"
        onClick={() => setRoomsDrawerOpen(true)}
        aria-expanded={roomsDrawerOpen}
        data-testid="room-drawer-toggle"
      >
        房間 {c.rooms.length > 0 ? c.rooms.length : ''}
      </button>
      {c.active?.room ? <span className="cui-mono cui-muted text-[11px]">在 {c.active.room}</span> : null}
    </div>
  );

  /**
   * 通知佇列：右上角固定一疊，多筆並存、各自可關（舊的單條橫幅會被下一筆蓋掉）。
   * `position: fixed`，所以不佔版面流、不推擠三欄。
   */
  const noticeStack = (
    <NoticeStack
      notices={c.notices}
      onDismiss={c.dismissNotice}
      onRetry={(notice) => {
        if (notice.retry) notice.retry();
        c.dismissNotice(notice.id);
      }}
      top={44}
    />
  );
  /**
   * 內容區最上方兩條：錯誤橫幅 ＋ API Token 輸入列。
   * token 列自己判斷要不要出現（有 token 時收成一顆小按鈕，mock 模式完全不渲染），
   * 所以這裡無條件放，不必把「有沒有 token」的狀態拉上來。
   */
  /**
   * 開通進度／失敗：放在內容區最上面（沿用 `topBars`），三種版面（桌面三欄、手機、整頁面板）
   * 都看得到。失敗時的重試按鈕就在同一塊，不必回頭找按鈕。
   *
   * 兩種位置：
   * - 已經有對話在看 → 上方細條（`bar`），不要蓋掉正在讀的對話；
   * - **還沒有任何對話**（開第一顆身分最常見）→ 主區那張卡片（`stage`），
   *   直接把原本 8–12 秒的整片空白填掉，而不是只在上方閃一條小字。
   */
  const openingBar =
    c.opening && c.active ? (
      <OpeningPanel
        variant="bar"
        service={c.opening.attempt.service}
        startedAt={c.opening.startedAt}
        status={c.opening.status}
        error={c.opening.error}
        label={c.opening.attempt.nickname}
        room={c.opening.attempt.room}
        onRetry={c.retryOpening}
        onDismiss={c.dismissOpening}
      />
    ) : null;

  const openingStage =
    c.opening && !c.active ? (
      <OpeningPanel
        variant="stage"
        service={c.opening.attempt.service}
        startedAt={c.opening.startedAt}
        status={c.opening.status}
        error={c.opening.error}
        label={c.opening.attempt.nickname}
        room={c.opening.attempt.room}
        onRetry={c.retryOpening}
        onDismiss={c.dismissOpening}
      />
    ) : null;

  const topBars = (
    <>
      {openingBar}
      {noticeStack}
      <SessionBar {...(session ?? {})} />
    </>
  );

  /**
   * 房間頁（`#/rooms`）：**單欄情境側欄**，左邊只有房間清單，右邊是選中那間房的對話。
   * 不再有第二份房間清單（以前這裡一份、收件匣抽屜又一份，數字各自漂移，#12）。
   */
  if (section === 'rooms') {
    if (narrow) {
      return (
        <>
          {topBars}
          <main className="flex min-h-0 flex-1 flex-col gap-2 p-2">
            {roomsToggle}
            {/*
              有身分就看得到那一房的對話，和桌機同一個條件（原本這裡多卡一個 `mobileThread`，
              而 `mobileThread` 只有 `openConversation` 會設：從抽屜點一間「還沒有已開對話」的房
              只會走 `joinRoom`，房間換了、標題也對了，主區卻永遠停在提示字）。
              返回＝把房間清單（抽屜）再打開，不是把對話藏起來。
            */}
            {c.active ? (
              <>
                <div className="inbox-slot">{thread(() => setRoomsDrawerOpen(true))}</div>
                {commandBar}
              </>
            ) : openingStage ? (
              <div className="inbox-slot">{openingStage}</div>
            ) : (
              <div className="inbox-slot inbox-slot--fit">
                <p className="cui-empty" data-testid="room-pick-hint">
                  選一間房就看那間房的對話（按上方「房間」）。
                </p>
              </div>
            )}
          </main>
          {roomsDrawerOpen ? roomsDrawer : null}
        </>
      );
    }
    return (
      <>
        {topBars}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside
            className="flex w-[300px] flex-none flex-col gap-2 border-r border-border p-2"
            data-testid="room-context-sidebar"
          >
            {roomSidebar}
          </aside>
          <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-2">
            {c.active ? (
              <>
                <div className="inbox-slot">{thread()}</div>
                {commandBar}
              </>
            ) : openingStage ? (
              <div className="inbox-slot">{openingStage}</div>
            ) : (
              <div className="inbox-slot inbox-slot--fit">
                <p className="cui-empty" data-testid="room-pick-hint">
                  選左邊一間房，右邊就顯示那間房的對話。
                </p>
              </div>
            )}
          </section>
        </div>
      </>
    );
  }

  /** 舊的「身分／憑證」整頁面板已下線（`#/identities` 是唯一入口）；這裡只留身分面板本體給其他頁用。 */
  if (section !== 'workspace') {
    return (
      <>
        {topBars}
        <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
          <div className="inbox-slot">{sessionsPanel}</div>
        </div>
      </>
    );
  }

  if (narrow) {
    return (
      <>
        {topBars}
        <main className="flex min-h-0 flex-1 flex-col gap-2 p-2">
          {roomsToggle}
          {openingStage ? (
            <div className="inbox-slot">{openingStage}</div>
          ) : c.mobileThread && c.active ? (
            <>
              <div className="inbox-slot">{thread(() => c.setMobileThread(false))}</div>
              {commandBar}
            </>
          ) : (
            <>
              <LeftRail console={c} />
              <div className="inbox-slot">{list}</div>
            </>
          )}
        </main>
        {roomsDrawerOpen ? roomsDrawer : null}
      </>
    );
  }

  return (
    <>
      {topBars}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="flex w-[300px] flex-none flex-col gap-2 border-r border-border p-2">
          <LeftRail console={c} />
          <div className="inbox-slot">{list}</div>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-2">
          {openingStage ? (
            <div className="inbox-slot">{openingStage}</div>
          ) : (
            <div className="inbox-slot">{thread()}</div>
          )}
          {commandBar}
        </section>

        {/* 右側情境側欄：只剩房間（健康分頁退場，連線狀態只在系統區）。 */}
        {c.drawerOpen ? (
          <aside
            className="flex w-[300px] flex-none flex-col gap-2 border-l border-border p-2"
            data-testid="room-drawer"
          >
            {roomSidebar}
          </aside>
        ) : null}
      </div>
    </>
  );
}
